import type {
  AiChat,
  Assignment,
  AssignmentReachPayload,
  AssignmentsAPIParams,
  AiChatsGetParams,
  AnalyticsWidgetsAPIParams,
  AuditLogsAPIParams,
  ChatbotAnalyticsPeriod,
  ChatbotUnansweredParams,
  Chat,
  CollectionStudentsGetParams,
  ContentProgressAnalyticsAPIParams,
  ContentRosterAPIParams,
  Course,
  CourseCollection,
  CourseCollectionsAPIParams,
  CoursesAPIParams,
  CourseStreamsGetParams,
  CourseTariffsGetParams,
  Exam,
  ExamNew,
  ExamSubmission,
  ExamSubmissionsGetParams,
  ExamSubmissionsOverviewGetParams,
  ExamSubmissionWidgetsGetParams,
  FeedbackAPIParams,
  FeedbackWidgetAPIParams,
  GetMembersParams,
  Group,
  GroupsGetParams,
  GroupStudentsGetParams,
  Homework,
  Lecture,
  LiveProvider,
  LiveSession,
  LiveSessionsGetParams,
  McpConnectionsGetParams,
  McpConsentRequest,
  Member,
  MemberProgressAnalyticsAPIParams,
  PaymentMethod,
  Platform,
  PlatformDocument,
  PlatformInvoicesParams,
  PracticeAttempt,
  PracticeResultsGetParams,
  PracticeResultsOverviewGetParams,
  PracticeResultsWidgetsGetParams,
  PointsAnalyticsAPIParams,
  SalesHistoryAPIParams,
  Stream,
  StreamStudentsGetParams,
  Student,
  StudentEngagementAPIParams,
  EmployeesAPIParams,
  StudentsAPIParams,
  Submission,
  SubmissionsGetParams,
  Survey,
  SurveyAnalyticsParams,
  SurveyResponsesGetParams,
  SurveysGetParams,
  SurveyTextAnswersGetParams,
  TrainingControlPeopleParams,
  TrainingControlScopeParams,
} from 'src/types';

// Every query key of the app, grouped by entity. Hooks build keys only through these.
//
// React Query matches keys by prefix, so the nesting is load-bearing: invalidating
// `courseKeys.all(platformId)` also refreshes that platform's lectures, exams, course streams,
// tariffs and public pages, because their keys extend it. Keep a new key under the prefix that
// should refresh it.
//
// - `root` — the entity on every platform. Only the AI assistant's `invalidates` uses it, because
//   tool definitions are static and have no platform id.
// - `all(platformId)` — the entity on one platform; what a mutation usually invalidates.
//
// Where an object has `root` or `all`, every other key in it extends them — a key that must not be
// refreshed with its entity gets its own object (see `groupStudentKeys`, `surveyResultKeys`).

type PlatformId = Platform['id'];
// Ids often come straight from `useParams()`; such a query is disabled until the id exists.
type Id<T extends { id: unknown }> = T['id'] | undefined;

// ---------------------------------------------------------------- platform

export const platformKeys = {
  list: () => ['platforms'] as const,
  // The detail lives under its own root, so refreshing the list leaves an open settings page alone.
  details: ['platform'] as const,
  detail: (platformId: PlatformId) => ['platform', platformId] as const,
};

export const platformContactKeys = {
  root: ['platform-contacts'] as const,
  all: (platformId: PlatformId) => ['platform-contacts', platformId] as const,
};

export const documentKeys = {
  root: ['documents'] as const,
  all: (platformId: PlatformId) => ['documents', platformId] as const,
  list: (platformId: PlatformId, search: string | undefined) =>
    ['documents', platformId, search] as const,
  detail: (platformId: PlatformId, documentId: Id<PlatformDocument>) =>
    ['documents', platformId, documentId] as const,
};

export const roleKeys = {
  root: ['roles'] as const,
  all: (platformId: PlatformId) => ['roles', platformId] as const,
};

export const auditLogKeys = {
  list: (platformId: PlatformId, params: AuditLogsAPIParams) =>
    ['audit-logs', platformId, params] as const,
};

export const telegramProfileKeys = {
  detail: () => ['telegram-profile'] as const,
};

export const invitationKeys = {
  detail: (invitationId: string | null) => ['invitation', invitationId] as const,
};

// ---------------------------------------------------------------- billing

export const subscriptionStatusKeys = {
  root: ['subscription-status'] as const,
  detail: (platformId: PlatformId) => ['subscription-status', platformId] as const,
};

export const platformInvoiceKeys = {
  root: ['platform-invoices'] as const,
  list: (platformId: PlatformId, params: PlatformInvoicesParams) =>
    ['platform-invoices', platformId, params] as const,
};

export const platformPaymentStatusKeys = {
  detail: (paymentId: string | null) => ['platform-payment-status', paymentId] as const,
};

export const paymentMethodKeys = {
  root: ['payment-methods'] as const,
  all: (platformId: PlatformId) => ['payment-methods', platformId] as const,
  detail: (platformId: PlatformId, paymentMethodId: Id<PaymentMethod>) =>
    ['payment-methods', platformId, paymentMethodId] as const,
};

export const salesHistoryKeys = {
  list: (platformId: PlatformId, params: SalesHistoryAPIParams) =>
    ['sales-history', platformId, params] as const,
  widgets: (platformId: PlatformId, params: SalesHistoryAPIParams) =>
    ['sales-history', 'widget', platformId, params] as const,
};

// ---------------------------------------------------------------- courses and their content

export const courseKeys = {
  root: ['courses'] as const,
  all: (platformId: PlatformId) => ['courses', platformId] as const,
  list: (platformId: PlatformId, params: CoursesAPIParams) =>
    ['courses', platformId, params] as const,
  infinite: (platformId: PlatformId, params: CoursesAPIParams) =>
    ['courses', platformId, params, 'infinite'] as const,
  // Outside `all(platformId)` on purpose: course edits don't refresh the sales filter.
  salesFilter: (platformId: PlatformId) => ['courses', 'sales-filter', platformId] as const,
  detail: (platformId: PlatformId, courseId: Id<Course>) =>
    ['courses', platformId, courseId] as const,
  emptyContent: (platformId: PlatformId, courseId: Id<Course>) =>
    ['courses', platformId, courseId, 'empty-content'] as const,
  gamification: (platformId: PlatformId, courseId: Id<Course>) =>
    ['courses', platformId, courseId, 'gamification'] as const,
  publicPage: (platformId: PlatformId, courseId: Id<Course>) =>
    ['courses', platformId, courseId, 'course-public-page'] as const,
  tariffs: (platformId: PlatformId, courseId: Id<Course>) =>
    ['courses', platformId, courseId, 'course-tariffs'] as const,
  tariffList: (platformId: PlatformId, courseId: Id<Course>, params: CourseTariffsGetParams) =>
    ['courses', platformId, courseId, 'course-tariffs', params] as const,
  streams: (platformId: PlatformId, courseId: Id<Course>) =>
    ['courses', platformId, courseId, 'streams'] as const,
  streamList: (platformId: PlatformId, courseId: Id<Course>, params: CourseStreamsGetParams) =>
    ['courses', platformId, courseId, 'streams', params] as const,
  indexStatus: (platformId: PlatformId, courseId: Id<Course>) =>
    ['courses', platformId, courseId, 'index-status'] as const,
};

// Lectures and exams live under `courseKeys.all(platformId)`, so any course mutation refreshes them.
export const lectureKeys = {
  detail: (platformId: PlatformId, lectureId: Id<Lecture>) =>
    [...courseKeys.all(platformId), 'lectures', lectureId] as const,
  content: (platformId: PlatformId, lectureId: Id<Lecture>) =>
    [...courseKeys.all(platformId), 'lectures', 'content', lectureId] as const,
  practice: (platformId: PlatformId, lectureId: Id<Lecture>) =>
    [...courseKeys.all(platformId), 'lectures', 'practice', lectureId] as const,
};

export const lectureAiReportKeys = {
  root: ['lecture-ai-report'] as const,
  detail: (platformId: PlatformId, lectureId: Id<Lecture>) =>
    ['lecture-ai-report', platformId, lectureId] as const,
};

export const lectureAiFixKeys = {
  root: ['lecture-ai-fixes'] as const,
  list: (platformId: PlatformId, lectureId: Id<Lecture>) =>
    ['lecture-ai-fixes', platformId, lectureId] as const,
};

export const videoKeys = {
  info: (videoId: string) => ['videoInfo', videoId] as const,
  posters: (videoId: string) => ['videoPosters', videoId] as const,
};

export const examKeys = {
  detail: (platformId: PlatformId, examId: Id<Exam>) =>
    [...courseKeys.all(platformId), 'exams', examId] as const,
  detailNew: (platformId: PlatformId, examId: Id<ExamNew>) =>
    [...courseKeys.all(platformId), 'exams', 'new', examId] as const,
  importStatus: (taskId: string | null) => ['exam-import-status', taskId] as const,
  generateFromLecturesStatus: (platformId: PlatformId, taskId: string | null) =>
    ['exam-generate-from-lectures', platformId, taskId] as const,
};

export const homeworkKeys = {
  root: ['homework'] as const,
  all: (platformId: PlatformId) => ['homework', platformId] as const,
  detail: (platformId: PlatformId, homeworkId: Id<Homework>) =>
    ['homework', platformId, homeworkId] as const,
};

export const categoryKeys = {
  root: ['categories'] as const,
  all: (platformId: PlatformId) => ['categories', platformId] as const,
};

export const courseCollectionKeys = {
  root: ['course-collections'] as const,
  all: (platformId: PlatformId) => ['course-collections', platformId] as const,
  list: (platformId: PlatformId, params: CourseCollectionsAPIParams) =>
    ['course-collections', platformId, params] as const,
  // Outside `all(platformId)` on purpose, like `courseKeys.salesFilter`.
  salesFilter: (platformId: PlatformId) =>
    ['course-collections', 'sales-filter', platformId] as const,
  detail: (platformId: PlatformId, collectionId: Id<CourseCollection>) =>
    ['course-collections', platformId, collectionId] as const,
  courses: (platformId: PlatformId, collectionId: Id<CourseCollection>) =>
    ['course-collections', platformId, collectionId, 'courses'] as const,
  students: (platformId: PlatformId, collectionId: Id<CourseCollection>) =>
    ['course-collections', platformId, collectionId, 'students'] as const,
  studentList: (
    platformId: PlatformId,
    collectionId: Id<CourseCollection>,
    params: CollectionStudentsGetParams
  ) => ['course-collections', platformId, collectionId, 'students', params] as const,
};

// ---------------------------------------------------------------- students, streams and groups

export const studentKeys = {
  root: ['students'] as const,
  all: (platformId: PlatformId) => ['students', platformId] as const,
  list: (platformId: PlatformId, params: StudentsAPIParams | EmployeesAPIParams) =>
    ['students', platformId, params] as const,
  detail: (platformId: PlatformId, studentId: Id<Student>) =>
    ['students', platformId, studentId] as const,
  allCourses: (platformId: PlatformId) => ['students', platformId, 'courses'] as const,
  courses: (platformId: PlatformId, studentId: Id<Student>) =>
    ['students', platformId, 'courses', studentId] as const,
  history: (platformId: PlatformId, studentId: Id<Student>) =>
    ['students', platformId, studentId, 'history'] as const,
  learning: (platformId: PlatformId, studentId: Id<Student>) =>
    ['students', platformId, studentId, 'learning'] as const,
  import: (platformId: PlatformId, importId: string) =>
    ['students', platformId, 'import', importId] as const,
};

// ---------------------------------------------------------------------------- org structure

export const orgUnitKeys = {
  root: ['org-units'] as const,
  all: (platformId: PlatformId) => ['org-units', platformId] as const,
  tree: (platformId: PlatformId) => ['org-units', platformId, 'tree'] as const,
};

export const positionKeys = {
  root: ['positions'] as const,
  all: (platformId: PlatformId) => ['positions', platformId] as const,
  list: (platformId: PlatformId) => ['positions', platformId, 'list'] as const,
};

// ---------------------------------------------------------------------------- assignments

export const assignmentKeys = {
  root: ['assignments'] as const,
  all: (platformId: PlatformId) => ['assignments', platformId] as const,
  list: (platformId: PlatformId, params: AssignmentsAPIParams) =>
    ['assignments', platformId, 'list', params] as const,
  detail: (platformId: PlatformId, assignmentId: Id<Assignment>) =>
    ['assignments', platformId, 'detail', assignmentId] as const,
  history: (platformId: PlatformId, assignmentId: Id<Assignment>) =>
    ['assignments', platformId, 'detail', assignmentId, 'history'] as const,
  reach: (platformId: PlatformId, payload: AssignmentReachPayload) =>
    ['assignments', platformId, 'reach', payload] as const,
};

export const streamKeys = {
  root: ['streams'] as const,
  detail: (platformId: PlatformId, streamId: Id<Stream>) =>
    ['streams', platformId, streamId] as const,
  settings: (platformId: PlatformId, streamId: Id<Stream>) =>
    ['streams', platformId, streamId, 'settings-new'] as const,
  groups: (platformId: PlatformId, streamId: Id<Stream>) =>
    ['streams', platformId, streamId, 'groups'] as const,
  groupList: (platformId: PlatformId, streamId: Id<Stream>, params: GroupsGetParams) =>
    ['streams', platformId, streamId, 'groups', params] as const,
  students: (platformId: PlatformId, streamId: Id<Stream>) =>
    ['streams', platformId, streamId, 'students'] as const,
  studentList: (platformId: PlatformId, streamId: Id<Stream>, params: StreamStudentsGetParams) =>
    ['streams', platformId, streamId, 'students', params] as const,
  unassignedStudents: (platformId: PlatformId, streamId: Id<Stream>) =>
    ['streams', platformId, streamId, 'students', 'unassigned'] as const,
};

export const groupKeys = {
  root: ['groups'] as const,
  detail: (platformId: PlatformId, groupId: Id<Group>) => ['groups', platformId, groupId] as const,
};

// A group's roster, keyed apart from `groupKeys` so that editing the group doesn't refetch it.
export const groupStudentKeys = {
  root: ['student-groups'] as const,
  all: (platformId: PlatformId, groupId: Id<Group>) =>
    ['student-groups', platformId, groupId, 'students'] as const,
  list: (platformId: PlatformId, groupId: Id<Group>, params: GroupStudentsGetParams) =>
    ['student-groups', platformId, groupId, 'students', params] as const,
  unassigned: (platformId: PlatformId, groupId: Id<Group>) =>
    ['student-groups', platformId, groupId, 'students', 'unassigned'] as const,
  unassignedList: (platformId: PlatformId, groupId: Id<Group>, params: GroupStudentsGetParams) =>
    ['student-groups', platformId, groupId, 'students', 'unassigned', params] as const,
  unassignedInfinite: (
    platformId: PlatformId,
    groupId: Id<Group>,
    params: GroupStudentsGetParams
  ) =>
    ['student-groups', platformId, groupId, 'students', 'unassigned', params, 'infinite'] as const,
};

export const memberKeys = {
  root: ['members'] as const,
  all: (platformId: PlatformId) => ['members', platformId] as const,
  list: (platformId: PlatformId, params: GetMembersParams) =>
    ['members', platformId, params] as const,
  invited: (platformId: PlatformId) => ['members', platformId, 'invited'] as const,
  detail: (platformId: PlatformId, memberId: Id<Member>) =>
    ['members', platformId, memberId] as const,
};

// ---------------------------------------------------------------- submissions and feedback

export const submissionKeys = {
  root: ['submissions'] as const,
  // Optimistic updates read the list params back at index 3 — keep `params` there.
  lists: (platformId: PlatformId) => ['submissions', platformId, 'list'] as const,
  list: (platformId: PlatformId, params: SubmissionsGetParams) =>
    ['submissions', platformId, 'list', params] as const,
  detail: (platformId: PlatformId, submissionId: Id<Submission>) =>
    ['submissions', platformId, submissionId] as const,
};

export const examSubmissionKeys = {
  list: (platformId: PlatformId, params: ExamSubmissionsGetParams) =>
    ['exam-submissions', platformId, 'new', 'list', params] as const,
  detail: (platformId: PlatformId, examSubmissionId: Id<ExamSubmission>) =>
    ['exam-submissions', platformId, 'new', examSubmissionId] as const,
  overview: (platformId: PlatformId, params: ExamSubmissionsOverviewGetParams) =>
    ['exam-submissions', platformId, 'overview', params] as const,
  widgets: (platformId: PlatformId, params: ExamSubmissionWidgetsGetParams) =>
    ['exam-submissions', platformId, 'widgets', params] as const,
};

export const practiceResultKeys = {
  root: ['practice-results'] as const,
  all: (platformId: PlatformId) => ['practice-results', platformId] as const,
  overview: (platformId: PlatformId, params: PracticeResultsOverviewGetParams) =>
    ['practice-results', platformId, 'overview', params] as const,
  practice: (platformId: PlatformId, lectureId: Id<Lecture>) =>
    ['practice-results', platformId, 'practice', lectureId] as const,
  list: (platformId: PlatformId, params: PracticeResultsGetParams) =>
    ['practice-results', platformId, 'list', params] as const,
  widgets: (platformId: PlatformId, params: PracticeResultsWidgetsGetParams) =>
    ['practice-results', platformId, 'widgets', params] as const,
  attempt: (platformId: PlatformId, attemptId: Id<PracticeAttempt>) =>
    ['practice-results', platformId, 'attempt', attemptId] as const,
};

export const feedbackKeys = {
  root: ['feedbacks'] as const,
  // Optimistic updates read the list params back at index 1 — keep `params` there.
  list: (params: FeedbackAPIParams) => ['feedbacks', params] as const,
  widget: (platformId: PlatformId, params: FeedbackWidgetAPIParams) =>
    ['feedbacks', 'widget', platformId, params] as const,
};

// ---------------------------------------------------------------- analytics

export const analyticsKeys = {
  root: ['analytics'] as const,
  widgets: (platformId: PlatformId, params: AnalyticsWidgetsAPIParams) =>
    ['analytics', platformId, 'widgets', params] as const,
  points: (platformId: PlatformId, params: PointsAnalyticsAPIParams) =>
    ['analytics', platformId, 'points', params] as const,
  contentProgress: (platformId: PlatformId, params: ContentProgressAnalyticsAPIParams) =>
    ['analytics', platformId, 'content-progress', params] as const,
  contentRoster: (platformId: PlatformId, params: ContentRosterAPIParams | null) =>
    ['analytics', platformId, 'content-roster', params] as const,
  memberProgress: (platformId: PlatformId, params: MemberProgressAnalyticsAPIParams) =>
    ['analytics', platformId, 'member-progress', params] as const,
  studentEngagement: (platformId: PlatformId, params: StudentEngagementAPIParams) =>
    ['analytics', platformId, 'student-engagement', params] as const,
  // One prefix per platform, so a change to employees or assignments can refresh the report.
  trainingControl: (platformId: PlatformId) =>
    ['analytics', platformId, 'training-control'] as const,
  trainingControlSummary: (platformId: PlatformId, params: TrainingControlScopeParams) =>
    ['analytics', platformId, 'training-control', 'summary', params] as const,
  trainingControlUnits: (platformId: PlatformId, params: TrainingControlScopeParams) =>
    ['analytics', platformId, 'training-control', 'units', params] as const,
  trainingControlPeople: (platformId: PlatformId, params: TrainingControlPeopleParams) =>
    ['analytics', platformId, 'training-control', 'people', params] as const,
};

// ---------------------------------------------------------------- surveys

export const surveyKeys = {
  root: ['surveys'] as const,
  all: (platformId: PlatformId) => ['surveys', platformId] as const,
  byCourse: (platformId: PlatformId, courseId: Id<Course>) =>
    ['surveys', platformId, courseId] as const,
  list: (platformId: PlatformId, courseId: Id<Course>, params: SurveysGetParams) =>
    ['surveys', platformId, courseId, params] as const,
  detail: (platformId: PlatformId, surveyId: Id<Survey>) =>
    ['surveys', platformId, surveyId] as const,
};

// Survey results are keyed apart from `surveyKeys`, so editing a survey doesn't refetch them.
export const surveyResultKeys = {
  responses: (platformId: PlatformId, surveyId: Id<Survey>, params: SurveyResponsesGetParams) =>
    ['survey-responses', platformId, surveyId, params] as const,
  analytics: (platformId: PlatformId, surveyId: Id<Survey>, params: SurveyAnalyticsParams) =>
    ['survey-analytics', platformId, surveyId, params] as const,
  textAnswers: (
    platformId: PlatformId,
    surveyId: Id<Survey>,
    questionId: string,
    params: SurveyTextAnswersGetParams
  ) => ['survey-text-answers', platformId, surveyId, questionId, params] as const,
};

// ---------------------------------------------------------------- chatbot

export const chatbotSettingsKeys = {
  root: ['chatbot-settings'] as const,
  detail: (platformId: PlatformId) => ['chatbot-settings', platformId] as const,
};

export const chatbotCourseKeys = {
  root: ['chatbot-courses'] as const,
  all: (platformId: PlatformId) => ['chatbot-courses', platformId] as const,
};

export const chatbotFaqKeys = {
  root: ['chatbot-faq'] as const,
  list: (platformId: PlatformId, courseId: Id<Course>) =>
    ['chatbot-faq', platformId, courseId] as const,
};

export const chatbotAnalyticsKeys = {
  overview: (platformId: PlatformId, period: ChatbotAnalyticsPeriod) =>
    ['chatbot-analytics', platformId, period] as const,
  unanswered: (platformId: PlatformId, params: ChatbotUnansweredParams) =>
    ['chatbot-analytics', platformId, 'unanswered', params] as const,
};

export const chatbotContextKeys = {
  root: ['chatbot-context'] as const,
  detail: (platformId: PlatformId) => ['chatbot-context', platformId] as const,
};

export const chatbotCourseContextKeys = {
  detail: (platformId: PlatformId, courseId: Id<Course>) =>
    ['chatbot-course-context', platformId, courseId] as const,
};

// ---------------------------------------------------------------- chats

export const chatKeys = {
  root: ['chats'] as const,
  list: (platformId: PlatformId) => ['chats', platformId] as const,
  unread: (platformId: PlatformId) => ['chats', platformId, 'unread'] as const,
  availableSources: (platformId: PlatformId) => ['chats', platformId, 'available-sources'] as const,
  contacts: (platformId: PlatformId, role: string, search: string) =>
    ['chats', platformId, 'contacts', role, search] as const,
  messages: (platformId: PlatformId, chatId: Id<Chat>) =>
    ['chats', platformId, chatId, 'messages'] as const,
  members: (platformId: PlatformId, chatId: Id<Chat>) =>
    ['chats', platformId, chatId, 'members'] as const,
  files: (platformId: PlatformId, chatId: Id<Chat>) =>
    ['chats', platformId, chatId, 'files'] as const,
};

export const aiChatKeys = {
  all: (platformId: PlatformId) => ['ai-chats', platformId] as const,
  list: (platformId: PlatformId, params: AiChatsGetParams) =>
    ['ai-chats', platformId, params] as const,
  detail: (platformId: PlatformId, chatId: Id<AiChat>) => ['ai-chats', platformId, chatId] as const,
};

// ---------------------------------------------------------------- live sessions

export const liveSessionKeys = {
  root: ['live-sessions'] as const,
  all: (platformId: PlatformId) => ['live-sessions', platformId] as const,
  list: (platformId: PlatformId, params: LiveSessionsGetParams) =>
    ['live-sessions', platformId, params] as const,
  detail: (platformId: PlatformId, sessionId: Id<LiveSession>) =>
    ['live-sessions', platformId, sessionId] as const,
};

export const liveAttendanceKeys = {
  detail: (platformId: PlatformId, sessionId: Id<LiveSession>) =>
    ['live-attendance', platformId, sessionId] as const,
};

export const liveAudienceOptionKeys = {
  all: (platformId: PlatformId) => ['live-audience-options', platformId] as const,
};

export const liveAccountKeys = {
  all: (platformId: PlatformId) => ['live-accounts', platformId] as const,
};

export const liveProviderSettingsKeys = {
  root: ['live-provider-settings'] as const,
  detail: (platformId: PlatformId, provider: LiveProvider) =>
    ['live-provider-settings', platformId, provider] as const,
};

// ---------------------------------------------------------------- AI integrations (MCP)

export const mcpKeys = {
  root: ['mcp'] as const,
  all: (platformId: PlatformId) => ['mcp', platformId] as const,
  access: (platformId: PlatformId) => ['mcp', platformId, 'access'] as const,
  settings: (platformId: PlatformId) => ['mcp', platformId, 'settings'] as const,
  connections: (platformId: PlatformId) => ['mcp', platformId, 'connections'] as const,
  connectionList: (platformId: PlatformId, params: McpConnectionsGetParams) =>
    ['mcp', platformId, 'connections', params] as const,
};

// Account-level: the user's own connections across every school they belong to.
export const mcpMyConnectionKeys = {
  root: ['mcp-my-connections'] as const,
  list: () => ['mcp-my-connections', 'list'] as const,
};

export const mcpConsentKeys = {
  detail: (requestId: McpConsentRequest['request_id'] | null) =>
    ['mcp-consent', requestId] as const,
};
